// The elli object is loaded globally via script tag in index.html

import {
  Resource,
  TransactionType,
  TransactionResponse,
} from "../types/epc-interfaces";
import { OriginationContext } from "../types/origination-context";
import { ElliScript } from "@/types/ellimae-types";

declare const elli: { script: ElliScript };

const waitForElli = (): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof elli !== "undefined") {
      resolve();
      return;
    }
    const checkElli = setInterval(() => {
      if (typeof elli !== "undefined") {
        clearInterval(checkElli);
        resolve();
      }
    }, 100);
  });
};

const ensureConnection = async () => {
  try {
    await waitForElli();
    elli.script.connect();
    await elli.script.ready();
    console.log("Host connected.");
    return true;
  } catch (error) {
    console.error("Error connecting to Encompass:", error);
    return false;
  }
};

const getOriginationContext = async (): Promise<OriginationContext> => {
  console.log("Host connected, getting transaction object...");
  const transactionObject = await elli.script.getObject("transaction");
  const origin = await transactionObject.getOrigin();

  const originationContext: OriginationContext = {
    originId: origin.id,
    partnerAccessToken: origin.partnerAccessToken,
    transactionId: origin.transactionId,
  };

  return originationContext;
};

/**
 * Closes the current interaction in Encompass.
 */
const closeInteraction = async (): Promise<void> => {
  const transactionObject = await elli.script.getObject("transaction");
  // Awaited, not fired: a caller that publishes "closed" before the host has
  // closed reports success it cannot know, and a host rejection would escape
  // as an unhandled rejection instead of the caller's error path.
  await transactionObject.close();
};

const setTransactionContext = async (transactionId: string) => {
  console.log("Setting transaction context...");
  const transactionObject = await elli.script.getObject("transaction");
  const transactionData = await transactionObject.set({ id: transactionId });
  if (transactionData.id != transactionId) {
    throw Error("setting transaction context failed");
  }
};

//TODO: Revisit this function as it is taking too many parameters is difficult to read and taking lot more than it needs
/**
 * @param requestType: Currently it can only hold Run LoanLight AUS as value
 * @param options: Options for the create(request) object from Ellimae API
 * @param resources: An Array containing all resources as part of the request
 * @param transactionId: If not provided it will create a new transaction or update it if it is desfined
 * @returns A new transactionId
 */
const submitTransaction = async ({
  requestType,
  resources,
  transactionId,
}: {
  requestType: TransactionType;
  resources: Resource[];
  transactionId: string | null;
}): Promise<string> => {
  if (!transactionId) {
    const transactionObject = await elli.script.getObject("transaction");
    const createRequest = {
      request: {
        type: requestType,
        resources,
      },
    };
    const transactionData: TransactionResponse =
      await transactionObject.create(createRequest);
    if (transactionData.id == null) throw Error("submitTransaction failed");

    return transactionData.id;
  } else {
    const transactionObject = await elli.script.getObject("transaction");
    const updateRequest = {
      request: {
        resources,
      },
    };
    const transactionData = await transactionObject.update(updateRequest);
    if (transactionData.id == null) throw Error("updateTransaction failed");

    return transactionData.id;
  }
};

const getAvailableResources = async () => {
  const applicationObject = await elli.script.getObject("application");
  return applicationObject.performAction("getAvailableResources", {
    allowedSourceTypes: ["esign", "efolder", "partner"],
  });
};

export {
  getOriginationContext,
  ensureConnection,
  closeInteraction,
  submitTransaction,
  getAvailableResources,
  setTransactionContext,
};
